import {
  base64UrlToBytes,
  bytesToBase64Url,
  randomBytes,
  utf8ToBytes
} from './bytes.ts';
import {
  combineSecretBytes,
  ShamirByteShare,
  splitSecretBytes
} from './shamir.ts';

/**
 * Client-side primitives for adult recovery circles.
 *
 * These primitives keep private keys non-extractable and do not log secrets.
 * They cannot stop a holder from copying a share after decryption, screenshots,
 * or malicious application code running in the same browser origin.
 */

export const RECOVERY_PROTOCOL_VERSION = 1 as const;
export const RECOVERY_MANIFEST_MAX_MEMBERS = 8;
export const RECOVERY_KEY_BYTES = 32;

const RECOVERY_SHARE_VERSION = 1 as const;
const RECOVERY_VAULT_ENVELOPE_VERSION = 1 as const;
const RECOVERY_SEAL_VERSION = 1 as const;
const AES_GCM_ALGORITHM = 'A256GCM' as const;
const RECOVERY_SEAL_SUITE = 'P256-HKDF-SHA256-A256GCM' as const;
const ECDH_P256 = { name: 'ECDH', namedCurve: 'P-256' } as const;
const AES_GCM_IV_BYTES = 12;
const HKDF_SALT_BYTES = 32;
const SEAL_ID_BYTES = 16;
const SHA256_BASE64URL_LENGTH = 43;
const MAX_ID_LENGTH = 128;
const MAX_SEALED_PLAINTEXT_BYTES = 16 * 1024;

type JsonRecord = Record<string, unknown>;

export type RecoveryHolderType = 'person' | 'institution';

export type RecoveryManifestMember = {
  membershipId: string;
  index: number;
  holderType: RecoveryHolderType;
  custodyPublicKeyFingerprint: string;
};

export type RecoveryManifest = {
  protocolVersion: typeof RECOVERY_PROTOCOL_VERSION;
  circleId: string;
  circleVersion: number;
  ownerId: string;
  threshold: number;
  total: number;
  createdAt: string;
  members: RecoveryManifestMember[];
};

export type RecoveryBinding = {
  protocolVersion: typeof RECOVERY_PROTOCOL_VERSION;
  circleId: string;
  circleVersion: number;
  manifestHash: string;
};

export type RecoveryVaultEnvelope = RecoveryBinding & {
  envelopeVersion: typeof RECOVERY_VAULT_ENVELOPE_VERSION;
  algorithm: typeof AES_GCM_ALGORITHM;
  iv: string;
  ciphertext: string;
};

export type RecoverySharePackage = RecoveryBinding & {
  shareVersion: typeof RECOVERY_SHARE_VERSION;
  membershipId: string;
  index: number;
  threshold: number;
  total: number;
  custodyPublicKeyFingerprint: string;
  shareValue: string;
};

export type RecoveryPublicKeyJwk = {
  kty: 'EC';
  crv: 'P-256';
  x: string;
  y: string;
};

export type RecoveryKeyPair = {
  publicKeyJwk: RecoveryPublicKeyJwk;
  publicKeyFingerprint: string;
  privateKey: CryptoKey;
};

export type RecoveryInitialShareContext = RecoveryBinding & {
  purpose: 'initial-share-custody';
  membershipId: string;
  index: number;
  custodyPublicKeyFingerprint: string;
};

export type RecoveryRequestType = 'owner-lockout' | 'incapacity';

export type RecoveryRequestDescriptor = {
  requestId: string;
  requestType: RecoveryRequestType;
  expiresAt: string;
};

export type RecoveryRequestShareContext = RecoveryBinding & {
  purpose: 'recovery-request-share';
  membershipId: string;
  index: number;
  custodyPublicKeyFingerprint: string;
  requestId: string;
  targetPublicKeyFingerprint: string;
  requestType: RecoveryRequestType;
  expiresAt: string;
};

export type RecoverySealContext =
  | RecoveryInitialShareContext
  | RecoveryRequestShareContext;

export type RecoverySealedShare = {
  sealVersion: typeof RECOVERY_SEAL_VERSION;
  protocolVersion: typeof RECOVERY_PROTOCOL_VERSION;
  suite: typeof RECOVERY_SEAL_SUITE;
  sealId: string;
  recipientPublicKeyFingerprint: string;
  ephemeralPublicKeyJwk: RecoveryPublicKeyJwk;
  hkdfSalt: string;
  iv: string;
  ciphertext: string;
  context: RecoverySealContext;
};

export interface RecoveryReplayGuard {
  /**
   * Must atomically return false if this request/member response was consumed
   * before. Durable integration should back this with persistent local state.
   */
  consume(replayKey: string): boolean | Promise<boolean>;
}

export type RecoveryCryptoErrorCode =
  | 'INVALID_INPUT'
  | 'CONTEXT_MISMATCH'
  | 'AUTHENTICATION_FAILED'
  | 'INSUFFICIENT_SHARES'
  | 'DUPLICATE_SHARE'
  | 'EXPIRED_REQUEST'
  | 'REPLAYED_REQUEST';

export class RecoveryCryptoError extends Error {
  readonly code: RecoveryCryptoErrorCode;

  constructor(code: RecoveryCryptoErrorCode, message: string) {
    super(message);
    this.name = 'RecoveryCryptoError';
    this.code = code;
  }
}

function invalid(message: string): never {
  throw new RecoveryCryptoError('INVALID_INPUT', message);
}

function asRecord(value: unknown, label: string): JsonRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    invalid(`${label} must be an object`);
  }
  return value as JsonRecord;
}

function assertExactKeys(record: JsonRecord, keys: readonly string[], label: string): void {
  const expected = new Set(keys);
  for (const key of Object.keys(record)) {
    if (!expected.has(key)) invalid(`${label} contains unknown field "${key}"`);
  }
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      invalid(`${label} is missing field "${key}"`);
    }
  }
}

function boundedString(value: unknown, label: string, maxLength = MAX_ID_LENGTH): string {
  if (
    typeof value !== 'string' ||
    value.length < 1 ||
    value.length > maxLength ||
    value.trim() !== value ||
    /[\u0000-\u001f\u007f]/.test(value)
  ) {
    invalid(`${label} must be a bounded, non-empty string`);
  }
  return value;
}

function positiveSafeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 1) {
    invalid(`${label} must be a positive safe integer`);
  }
  return value as number;
}

function canonicalTimestamp(value: unknown, label: string): string {
  const timestamp = boundedString(value, label, 32);
  const date = new Date(timestamp);
  if (!Number.isFinite(date.getTime()) || date.toISOString() !== timestamp) {
    invalid(`${label} must be a canonical UTC ISO timestamp`);
  }
  return timestamp;
}

function sha256Fingerprint(value: unknown, label: string): string {
  const fingerprint = boundedString(value, label, SHA256_BASE64URL_LENGTH);
  if (
    fingerprint.length !== SHA256_BASE64URL_LENGTH ||
    !/^[A-Za-z0-9_-]+$/.test(fingerprint)
  ) {
    invalid(`${label} must be a base64url SHA-256 fingerprint`);
  }
  const decoded = decodeBase64Url(fingerprint, label, 32);
  decoded.fill(0);
  return fingerprint;
}

function decodeBase64Url(
  value: unknown,
  label: string,
  expectedLength?: number,
  maxLength?: number
): Uint8Array {
  if (typeof value !== 'string' || value.length === 0 || !/^[A-Za-z0-9_-]+$/.test(value)) {
    invalid(`${label} must be unpadded base64url`);
  }
  let decoded: Uint8Array;
  try {
    decoded = base64UrlToBytes(value);
  } catch {
    invalid(`${label} must be valid base64url`);
  }
  if (bytesToBase64Url(decoded) !== value) {
    decoded.fill(0);
    invalid(`${label} is not canonical base64url`);
  }
  if (expectedLength !== undefined && decoded.length !== expectedLength) {
    decoded.fill(0);
    invalid(`${label} must encode ${expectedLength} bytes`);
  }
  if (maxLength !== undefined && decoded.length > maxLength) {
    decoded.fill(0);
    invalid(`${label} is too large`);
  }
  return decoded;
}

function isUint8Array(value: unknown): value is Uint8Array {
  return (
    ArrayBuffer.isView(value) &&
    Object.prototype.toString.call(value) === '[object Uint8Array]'
  );
}

function assertBytes(value: unknown, length: number, label: string): asserts value is Uint8Array {
  if (!isUint8Array(value) || value.length !== length) {
    invalid(`${label} must be exactly ${length} bytes`);
  }
}

function validateManifestMember(value: unknown): RecoveryManifestMember {
  const member = asRecord(value, 'Recovery manifest member');
  assertExactKeys(
    member,
    ['membershipId', 'index', 'holderType', 'custodyPublicKeyFingerprint'],
    'Recovery manifest member'
  );
  const holderType = member.holderType;
  if (holderType !== 'person' && holderType !== 'institution') {
    invalid('Recovery manifest holderType must be "person" or "institution"');
  }
  return {
    membershipId: boundedString(member.membershipId, 'Recovery membershipId'),
    index: positiveSafeInteger(member.index, 'Recovery member index'),
    holderType,
    custodyPublicKeyFingerprint: sha256Fingerprint(
      member.custodyPublicKeyFingerprint,
      'Recovery custody public-key fingerprint'
    )
  };
}

export function validateRecoveryManifest(value: unknown): RecoveryManifest {
  const manifest = asRecord(value, 'Recovery manifest');
  assertExactKeys(
    manifest,
    [
      'protocolVersion',
      'circleId',
      'circleVersion',
      'ownerId',
      'threshold',
      'total',
      'createdAt',
      'members'
    ],
    'Recovery manifest'
  );
  if (manifest.protocolVersion !== RECOVERY_PROTOCOL_VERSION) {
    invalid('Unsupported recovery protocol version');
  }
  const threshold = positiveSafeInteger(manifest.threshold, 'Recovery threshold');
  const total = positiveSafeInteger(manifest.total, 'Recovery total');
  if (threshold < 2 || threshold > total || total > RECOVERY_MANIFEST_MAX_MEMBERS) {
    invalid('Recovery threshold must satisfy 2 <= threshold <= total <= 8');
  }
  if (!Array.isArray(manifest.members) || manifest.members.length !== total) {
    invalid('Recovery manifest must contain exactly total member entries');
  }

  const members = manifest.members.map(validateManifestMember).sort((a, b) => a.index - b.index);
  const membershipIds = new Set<string>();
  for (let position = 0; position < members.length; position++) {
    const member = members[position];
    if (member.index !== position + 1) {
      invalid('Recovery member indexes must be the exact unique range 1..total');
    }
    if (membershipIds.has(member.membershipId)) {
      invalid('Recovery membershipIds must be unique');
    }
    membershipIds.add(member.membershipId);
  }

  return {
    protocolVersion: RECOVERY_PROTOCOL_VERSION,
    circleId: boundedString(manifest.circleId, 'Recovery circleId'),
    circleVersion: positiveSafeInteger(manifest.circleVersion, 'Recovery circleVersion'),
    ownerId: boundedString(manifest.ownerId, 'Recovery ownerId'),
    threshold,
    total,
    createdAt: canonicalTimestamp(manifest.createdAt, 'Recovery createdAt'),
    members
  };
}

export function canonicalSerializeRecoveryManifest(value: unknown): string {
  const manifest = validateRecoveryManifest(value);
  return JSON.stringify({
    protocolVersion: manifest.protocolVersion,
    circleId: manifest.circleId,
    circleVersion: manifest.circleVersion,
    ownerId: manifest.ownerId,
    threshold: manifest.threshold,
    total: manifest.total,
    createdAt: manifest.createdAt,
    members: manifest.members.map((member) => ({
      membershipId: member.membershipId,
      index: member.index,
      holderType: member.holderType,
      custodyPublicKeyFingerprint: member.custodyPublicKeyFingerprint
    }))
  });
}

async function digestSha256(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToBase64Url(new Uint8Array(digest));
}

export async function hashRecoveryManifest(value: unknown): Promise<string> {
  const encoded = utf8ToBytes(canonicalSerializeRecoveryManifest(value));
  try {
    return await digestSha256(encoded);
  } finally {
    encoded.fill(0);
  }
}

export async function fingerprintRecoveryManifest(value: unknown): Promise<string> {
  return hashRecoveryManifest(value);
}

function normalizePublicKey(value: unknown): RecoveryPublicKeyJwk {
  const jwk = asRecord(value, 'Recovery public key JWK');
  const allowed = new Set(['kty', 'crv', 'x', 'y', 'ext', 'key_ops']);
  for (const key of Object.keys(jwk)) {
    if (!allowed.has(key)) invalid(`Recovery public key JWK contains unknown field "${key}"`);
  }
  for (const key of ['kty', 'crv', 'x', 'y']) {
    if (!Object.prototype.hasOwnProperty.call(jwk, key)) {
      invalid(`Recovery public key JWK is missing field "${key}"`);
    }
  }
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256') {
    invalid('Recovery public key must be an EC P-256 key');
  }
  if (jwk.ext !== undefined && typeof jwk.ext !== 'boolean') {
    invalid('Recovery public key ext must be boolean');
  }
  if (
    jwk.key_ops !== undefined &&
    (!Array.isArray(jwk.key_ops) || jwk.key_ops.some((operation) => typeof operation !== 'string'))
  ) {
    invalid('Recovery public key key_ops must be a string array');
  }
  const xBytes = decodeBase64Url(jwk.x, 'Recovery public key x', 32);
  const yBytes = decodeBase64Url(jwk.y, 'Recovery public key y', 32);
  xBytes.fill(0);
  yBytes.fill(0);
  return { kty: 'EC', crv: 'P-256', x: jwk.x as string, y: jwk.y as string };
}

export function validateRecoveryPublicKey(value: unknown): RecoveryPublicKeyJwk {
  return normalizePublicKey(value);
}

export async function fingerprintRecoveryPublicKey(value: unknown): Promise<string> {
  const publicKey = normalizePublicKey(value);
  // RFC 7638 member selection and lexicographic member order.
  const encoded = utf8ToBytes(
    JSON.stringify({
      crv: publicKey.crv,
      kty: publicKey.kty,
      x: publicKey.x,
      y: publicKey.y
    })
  );
  try {
    return await digestSha256(encoded);
  } finally {
    encoded.fill(0);
  }
}

export async function generateRecoveryKeyPair(): Promise<RecoveryKeyPair> {
  const pair = (await crypto.subtle.generateKey(
    ECDH_P256,
    false,
    ['deriveBits']
  )) as CryptoKeyPair;
  if (pair.privateKey.extractable) {
    throw new RecoveryCryptoError(
      'INVALID_INPUT',
      'Browser generated an unexpectedly extractable recovery private key'
    );
  }
  const publicKeyJwk = normalizePublicKey(await crypto.subtle.exportKey('jwk', pair.publicKey));
  return {
    publicKeyJwk,
    publicKeyFingerprint: await fingerprintRecoveryPublicKey(publicKeyJwk),
    privateKey: pair.privateKey
  };
}

export async function generateCustodyKeyPair(): Promise<RecoveryKeyPair> {
  return generateRecoveryKeyPair();
}

export async function generateRecoveryTargetKeyPair(): Promise<RecoveryKeyPair> {
  return generateRecoveryKeyPair();
}

export function generateRecoveryKey(): Uint8Array {
  return randomBytes(RECOVERY_KEY_BYTES);
}

export async function createRecoveryBinding(value: unknown): Promise<RecoveryBinding> {
  const manifest = validateRecoveryManifest(value);
  return {
    protocolVersion: RECOVERY_PROTOCOL_VERSION,
    circleId: manifest.circleId,
    circleVersion: manifest.circleVersion,
    manifestHash: await hashRecoveryManifest(manifest)
  };
}

function validateRecoveryBinding(value: unknown, label: string): RecoveryBinding {
  const binding = asRecord(value, label);
  assertExactKeys(
    binding,
    ['protocolVersion', 'circleId', 'circleVersion', 'manifestHash'],
    label
  );
  if (binding.protocolVersion !== RECOVERY_PROTOCOL_VERSION) {
    invalid(`Unsupported ${label} protocol version`);
  }
  return {
    protocolVersion: RECOVERY_PROTOCOL_VERSION,
    circleId: boundedString(binding.circleId, `${label} circleId`),
    circleVersion: positiveSafeInteger(binding.circleVersion, `${label} circleVersion`),
    manifestHash: sha256Fingerprint(binding.manifestHash, `${label} manifest hash`)
  };
}

function bindingsMatch(left: RecoveryBinding, right: RecoveryBinding): boolean {
  return (
    left.protocolVersion === right.protocolVersion &&
    left.circleId === right.circleId &&
    left.circleVersion === right.circleVersion &&
    left.manifestHash === right.manifestHash
  );
}

function vaultAad(envelope: Omit<RecoveryVaultEnvelope, 'ciphertext'>): Uint8Array {
  return utf8ToBytes(
    JSON.stringify({
      domain: 'farfar-adult-vault-wrap-v1',
      envelopeVersion: envelope.envelopeVersion,
      algorithm: envelope.algorithm,
      protocolVersion: envelope.protocolVersion,
      circleId: envelope.circleId,
      circleVersion: envelope.circleVersion,
      manifestHash: envelope.manifestHash,
      iv: envelope.iv
    })
  );
}

export function validateRecoveryVaultEnvelope(value: unknown): RecoveryVaultEnvelope {
  const envelope = asRecord(value, 'Recovery vault envelope');
  if (
    (typeof envelope.domain === 'string' && envelope.domain.startsWith('farfar-access-'))
    || envelope.protocolDomain === 'court-access'
    || isCourtAccessPurpose(envelope.purpose)
  ) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Court-access envelopes cannot be used for recovery'
    );
  }
  assertExactKeys(
    envelope,
    [
      'envelopeVersion',
      'algorithm',
      'protocolVersion',
      'circleId',
      'circleVersion',
      'manifestHash',
      'iv',
      'ciphertext'
    ],
    'Recovery vault envelope'
  );
  if (
    envelope.envelopeVersion !== RECOVERY_VAULT_ENVELOPE_VERSION ||
    envelope.algorithm !== AES_GCM_ALGORITHM
  ) {
    invalid('Unsupported recovery vault envelope');
  }
  const binding = validateRecoveryBinding(
    {
      protocolVersion: envelope.protocolVersion,
      circleId: envelope.circleId,
      circleVersion: envelope.circleVersion,
      manifestHash: envelope.manifestHash
    },
    'Recovery vault binding'
  );
  const iv = decodeBase64Url(envelope.iv, 'Recovery vault IV', AES_GCM_IV_BYTES);
  const ciphertext = decodeBase64Url(
    envelope.ciphertext,
    'Recovery vault ciphertext',
    RECOVERY_KEY_BYTES + 16
  );
  iv.fill(0);
  ciphertext.fill(0);
  return {
    envelopeVersion: RECOVERY_VAULT_ENVELOPE_VERSION,
    algorithm: AES_GCM_ALGORITHM,
    ...binding,
    iv: envelope.iv as string,
    ciphertext: envelope.ciphertext as string
  };
}

async function importRecoveryAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function wrapAdultVaultKey(
  adultVaultKey: Uint8Array,
  recoveryKey: Uint8Array,
  manifestValue: unknown
): Promise<RecoveryVaultEnvelope> {
  assertBytes(adultVaultKey, RECOVERY_KEY_BYTES, 'Adult vault key');
  assertBytes(recoveryKey, RECOVERY_KEY_BYTES, 'Recovery key');
  const binding = await createRecoveryBinding(manifestValue);
  const iv = randomBytes(AES_GCM_IV_BYTES);
  const header: Omit<RecoveryVaultEnvelope, 'ciphertext'> = {
    envelopeVersion: RECOVERY_VAULT_ENVELOPE_VERSION,
    algorithm: AES_GCM_ALGORITHM,
    ...binding,
    iv: bytesToBase64Url(iv)
  };
  const aad = vaultAad(header);
  try {
    const key = await importRecoveryAesKey(recoveryKey);
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
      key,
      adultVaultKey
    );
    return {
      ...header,
      ciphertext: bytesToBase64Url(new Uint8Array(ciphertext))
    };
  } finally {
    iv.fill(0);
    aad.fill(0);
  }
}

async function unwrapAdultVaultKeyWithBinding(
  envelopeValue: unknown,
  recoveryKey: Uint8Array,
  expectedBinding: RecoveryBinding
): Promise<Uint8Array> {
  assertBytes(recoveryKey, RECOVERY_KEY_BYTES, 'Recovery key');
  const envelope = validateRecoveryVaultEnvelope(envelopeValue);
  const envelopeBinding: RecoveryBinding = {
    protocolVersion: envelope.protocolVersion,
    circleId: envelope.circleId,
    circleVersion: envelope.circleVersion,
    manifestHash: envelope.manifestHash
  };
  if (!bindingsMatch(envelopeBinding, expectedBinding)) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Recovery vault envelope does not match the expected circle manifest'
    );
  }

  const iv = decodeBase64Url(envelope.iv, 'Recovery vault IV', AES_GCM_IV_BYTES);
  const ciphertext = decodeBase64Url(
    envelope.ciphertext,
    'Recovery vault ciphertext',
    RECOVERY_KEY_BYTES + 16
  );
  const aad = vaultAad({
    envelopeVersion: envelope.envelopeVersion,
    algorithm: envelope.algorithm,
    ...envelopeBinding,
    iv: envelope.iv
  });
  try {
    const key = await importRecoveryAesKey(recoveryKey);
    let plaintext: ArrayBuffer;
    try {
      plaintext = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
        key,
        ciphertext
      );
    } catch {
      throw new RecoveryCryptoError(
        'AUTHENTICATION_FAILED',
        'Recovery vault authentication failed'
      );
    }
    const vaultKey = new Uint8Array(plaintext);
    if (vaultKey.length !== RECOVERY_KEY_BYTES) {
      vaultKey.fill(0);
      invalid('Recovery vault did not contain a 32-byte key');
    }
    return vaultKey;
  } finally {
    iv.fill(0);
    ciphertext.fill(0);
    aad.fill(0);
  }
}

export async function unwrapAdultVaultKey(
  envelopeValue: unknown,
  recoveryKey: Uint8Array,
  manifestValue: unknown
): Promise<Uint8Array> {
  return unwrapAdultVaultKeyWithBinding(
    envelopeValue,
    recoveryKey,
    await createRecoveryBinding(manifestValue)
  );
}

export function validateRecoverySharePackage(value: unknown): RecoverySharePackage {
  const share = asRecord(value, 'Recovery share package');
  assertExactKeys(
    share,
    [
      'shareVersion',
      'protocolVersion',
      'circleId',
      'circleVersion',
      'manifestHash',
      'membershipId',
      'index',
      'threshold',
      'total',
      'custodyPublicKeyFingerprint',
      'shareValue'
    ],
    'Recovery share package'
  );
  if (share.shareVersion !== RECOVERY_SHARE_VERSION) {
    invalid('Unsupported recovery share version');
  }
  const binding = validateRecoveryBinding(
    {
      protocolVersion: share.protocolVersion,
      circleId: share.circleId,
      circleVersion: share.circleVersion,
      manifestHash: share.manifestHash
    },
    'Recovery share binding'
  );
  const threshold = positiveSafeInteger(share.threshold, 'Recovery share threshold');
  const total = positiveSafeInteger(share.total, 'Recovery share total');
  if (threshold < 2 || threshold > total || total > RECOVERY_MANIFEST_MAX_MEMBERS) {
    invalid('Recovery share threshold must satisfy 2 <= threshold <= total <= 8');
  }
  const index = positiveSafeInteger(share.index, 'Recovery share index');
  if (index > total) invalid('Recovery share index exceeds total');
  const decoded = decodeBase64Url(
    share.shareValue,
    'Recovery share value',
    RECOVERY_KEY_BYTES
  );
  decoded.fill(0);
  return {
    shareVersion: RECOVERY_SHARE_VERSION,
    ...binding,
    membershipId: boundedString(share.membershipId, 'Recovery share membershipId'),
    index,
    threshold,
    total,
    custodyPublicKeyFingerprint: sha256Fingerprint(
      share.custodyPublicKeyFingerprint,
      'Recovery share custody public-key fingerprint'
    ),
    shareValue: share.shareValue as string
  };
}

function canonicalSerializeShare(shareValue: unknown): string {
  const share = validateRecoverySharePackage(shareValue);
  return JSON.stringify({
    shareVersion: share.shareVersion,
    protocolVersion: share.protocolVersion,
    circleId: share.circleId,
    circleVersion: share.circleVersion,
    manifestHash: share.manifestHash,
    membershipId: share.membershipId,
    index: share.index,
    threshold: share.threshold,
    total: share.total,
    custodyPublicKeyFingerprint: share.custodyPublicKeyFingerprint,
    shareValue: share.shareValue
  });
}

async function assertShareMatchesManifest(
  share: RecoverySharePackage,
  manifestValue: unknown
): Promise<RecoveryManifest> {
  const manifest = validateRecoveryManifest(manifestValue);
  const binding = await createRecoveryBinding(manifest);
  const member = manifest.members.find((entry) => entry.index === share.index);
  if (
    !bindingsMatch(share, binding) ||
    share.threshold !== manifest.threshold ||
    share.total !== manifest.total ||
    !member ||
    member.membershipId !== share.membershipId ||
    member.custodyPublicKeyFingerprint !== share.custodyPublicKeyFingerprint
  ) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Recovery share does not match the expected circle manifest'
    );
  }
  return manifest;
}

export async function splitRecoveryKey(
  recoveryKey: Uint8Array,
  manifestValue: unknown
): Promise<RecoverySharePackage[]> {
  assertBytes(recoveryKey, RECOVERY_KEY_BYTES, 'Recovery key');
  const manifest = validateRecoveryManifest(manifestValue);
  const binding = await createRecoveryBinding(manifest);
  const rawShares = splitSecretBytes(
    recoveryKey,
    manifest.total,
    manifest.threshold,
    manifest.members.map((member) => member.index)
  );
  return rawShares.map((rawShare, position) => {
    const member = manifest.members[position];
    try {
      return {
        shareVersion: RECOVERY_SHARE_VERSION,
        ...binding,
        membershipId: member.membershipId,
        index: rawShare.index,
        threshold: manifest.threshold,
        total: manifest.total,
        custodyPublicKeyFingerprint: member.custodyPublicKeyFingerprint,
        shareValue: bytesToBase64Url(rawShare.value)
      };
    } finally {
      rawShare.value.fill(0);
    }
  });
}

function validateInitialContext(value: unknown): RecoveryInitialShareContext {
  const context = asRecord(value, 'Initial recovery share context');
  assertExactKeys(
    context,
    [
      'purpose',
      'protocolVersion',
      'circleId',
      'circleVersion',
      'manifestHash',
      'membershipId',
      'index',
      'custodyPublicKeyFingerprint'
    ],
    'Initial recovery share context'
  );
  if (context.purpose !== 'initial-share-custody') {
    invalid('Invalid initial recovery share purpose');
  }
  const binding = validateRecoveryBinding(
    {
      protocolVersion: context.protocolVersion,
      circleId: context.circleId,
      circleVersion: context.circleVersion,
      manifestHash: context.manifestHash
    },
    'Initial recovery share binding'
  );
  return {
    purpose: 'initial-share-custody',
    ...binding,
    membershipId: boundedString(context.membershipId, 'Initial recovery membershipId'),
    index: positiveSafeInteger(context.index, 'Initial recovery share index'),
    custodyPublicKeyFingerprint: sha256Fingerprint(
      context.custodyPublicKeyFingerprint,
      'Initial recovery custody public-key fingerprint'
    )
  };
}

function validateRequestDescriptor(value: unknown): RecoveryRequestDescriptor {
  const request = asRecord(value, 'Recovery request');
  assertExactKeys(request, ['requestId', 'requestType', 'expiresAt'], 'Recovery request');
  if (request.requestType !== 'owner-lockout' && request.requestType !== 'incapacity') {
    invalid('Unsupported recovery request type');
  }
  return {
    requestId: boundedString(request.requestId, 'Recovery requestId'),
    requestType: request.requestType,
    expiresAt: canonicalTimestamp(request.expiresAt, 'Recovery request expiry')
  };
}

function validateRequestContext(value: unknown): RecoveryRequestShareContext {
  const context = asRecord(value, 'Recovery request share context');
  assertExactKeys(
    context,
    [
      'purpose',
      'protocolVersion',
      'circleId',
      'circleVersion',
      'manifestHash',
      'membershipId',
      'index',
      'custodyPublicKeyFingerprint',
      'requestId',
      'targetPublicKeyFingerprint',
      'requestType',
      'expiresAt'
    ],
    'Recovery request share context'
  );
  if (context.purpose !== 'recovery-request-share') {
    invalid('Invalid recovery request share purpose');
  }
  const binding = validateRecoveryBinding(
    {
      protocolVersion: context.protocolVersion,
      circleId: context.circleId,
      circleVersion: context.circleVersion,
      manifestHash: context.manifestHash
    },
    'Recovery request share binding'
  );
  const request = validateRequestDescriptor({
    requestId: context.requestId,
    requestType: context.requestType,
    expiresAt: context.expiresAt
  });
  return {
    purpose: 'recovery-request-share',
    ...binding,
    membershipId: boundedString(context.membershipId, 'Recovery request membershipId'),
    index: positiveSafeInteger(context.index, 'Recovery request share index'),
    custodyPublicKeyFingerprint: sha256Fingerprint(
      context.custodyPublicKeyFingerprint,
      'Recovery request custody public-key fingerprint'
    ),
    requestId: request.requestId,
    targetPublicKeyFingerprint: sha256Fingerprint(
      context.targetPublicKeyFingerprint,
      'Recovery target public-key fingerprint'
    ),
    requestType: request.requestType,
    expiresAt: request.expiresAt
  };
}

export function isCourtAccessPurpose(purpose: unknown): boolean {
  return typeof purpose === 'string' && (
    purpose === 'court-access' || purpose.startsWith('court-access-')
  );
}

export function validateRecoverySealContext(value: unknown): RecoverySealContext {
  const context = asRecord(value, 'Recovery seal context');
  if (isCourtAccessPurpose(context.purpose)) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Court-access seals cannot be used for recovery'
    );
  }
  if (context.purpose === 'initial-share-custody') return validateInitialContext(context);
  if (context.purpose === 'recovery-request-share') return validateRequestContext(context);
  invalid('Unsupported recovery seal context purpose');
}

function canonicalSerializeContext(value: unknown): string {
  const context = validateRecoverySealContext(value);
  if (context.purpose === 'initial-share-custody') {
    return JSON.stringify({
      purpose: context.purpose,
      protocolVersion: context.protocolVersion,
      circleId: context.circleId,
      circleVersion: context.circleVersion,
      manifestHash: context.manifestHash,
      membershipId: context.membershipId,
      index: context.index,
      custodyPublicKeyFingerprint: context.custodyPublicKeyFingerprint
    });
  }
  return JSON.stringify({
    purpose: context.purpose,
    protocolVersion: context.protocolVersion,
    circleId: context.circleId,
    circleVersion: context.circleVersion,
    manifestHash: context.manifestHash,
    membershipId: context.membershipId,
    index: context.index,
    custodyPublicKeyFingerprint: context.custodyPublicKeyFingerprint,
    requestId: context.requestId,
    targetPublicKeyFingerprint: context.targetPublicKeyFingerprint,
    requestType: context.requestType,
    expiresAt: context.expiresAt
  });
}

function contextFromShare(share: RecoverySharePackage): RecoveryInitialShareContext {
  return {
    purpose: 'initial-share-custody',
    protocolVersion: share.protocolVersion,
    circleId: share.circleId,
    circleVersion: share.circleVersion,
    manifestHash: share.manifestHash,
    membershipId: share.membershipId,
    index: share.index,
    custodyPublicKeyFingerprint: share.custodyPublicKeyFingerprint
  };
}

export async function createInitialShareContext(
  manifestValue: unknown,
  membershipId: string
): Promise<RecoveryInitialShareContext> {
  const manifest = validateRecoveryManifest(manifestValue);
  const normalizedMembershipId = boundedString(membershipId, 'Recovery membershipId');
  const member = manifest.members.find((entry) => entry.membershipId === normalizedMembershipId);
  if (!member) invalid('Recovery membershipId is not in the manifest');
  return {
    purpose: 'initial-share-custody',
    ...(await createRecoveryBinding(manifest)),
    membershipId: member.membershipId,
    index: member.index,
    custodyPublicKeyFingerprint: member.custodyPublicKeyFingerprint
  };
}

export async function createRecoveryRequestContext(
  manifestValue: unknown,
  membershipId: string,
  targetPublicKeyValue: unknown,
  requestValue: unknown
): Promise<RecoveryRequestShareContext> {
  const initial = await createInitialShareContext(manifestValue, membershipId);
  const request = validateRequestDescriptor(requestValue);
  return {
    purpose: 'recovery-request-share',
    protocolVersion: initial.protocolVersion,
    circleId: initial.circleId,
    circleVersion: initial.circleVersion,
    manifestHash: initial.manifestHash,
    membershipId: initial.membershipId,
    index: initial.index,
    custodyPublicKeyFingerprint: initial.custodyPublicKeyFingerprint,
    requestId: request.requestId,
    targetPublicKeyFingerprint: await fingerprintRecoveryPublicKey(targetPublicKeyValue),
    requestType: request.requestType,
    expiresAt: request.expiresAt
  };
}

function sealedShareAad(envelope: Omit<RecoverySealedShare, 'ciphertext'>): Uint8Array {
  return utf8ToBytes(
    JSON.stringify({
      domain: 'farfar-recovery-share-seal-v1',
      sealVersion: envelope.sealVersion,
      protocolVersion: envelope.protocolVersion,
      suite: envelope.suite,
      sealId: envelope.sealId,
      recipientPublicKeyFingerprint: envelope.recipientPublicKeyFingerprint,
      ephemeralPublicKeyJwk: envelope.ephemeralPublicKeyJwk,
      hkdfSalt: envelope.hkdfSalt,
      iv: envelope.iv,
      context: JSON.parse(canonicalSerializeContext(envelope.context))
    })
  );
}

async function sealKdfInfo(
  recipientFingerprint: string,
  ephemeralPublicKey: RecoveryPublicKeyJwk,
  context: RecoverySealContext
): Promise<Uint8Array> {
  return utf8ToBytes(
    JSON.stringify({
      domain: 'farfar-recovery-share-kdf-v1',
      protocolVersion: RECOVERY_PROTOCOL_VERSION,
      suite: RECOVERY_SEAL_SUITE,
      recipientPublicKeyFingerprint: recipientFingerprint,
      ephemeralPublicKeyFingerprint: await fingerprintRecoveryPublicKey(ephemeralPublicKey),
      context: JSON.parse(canonicalSerializeContext(context))
    })
  );
}

async function importP256PublicKey(value: unknown): Promise<CryptoKey> {
  const publicKey = normalizePublicKey(value);
  try {
    return await crypto.subtle.importKey('jwk', publicKey, ECDH_P256, false, []);
  } catch {
    invalid('Recovery public key is not a valid P-256 point');
  }
}

function assertP256PrivateKey(key: CryptoKey): void {
  const candidate = key as CryptoKey | undefined;
  const algorithm = candidate && (candidate.algorithm as KeyAlgorithm & { namedCurve?: string });
  if (
    !candidate ||
    candidate.type !== 'private' ||
    !algorithm ||
    algorithm.name !== 'ECDH' ||
    algorithm.namedCurve !== 'P-256' ||
    !candidate.usages.includes('deriveBits')
  ) {
    invalid('Recovery private key must be a P-256 ECDH deriveBits key');
  }
}

async function deriveSealKey(
  privateKey: CryptoKey,
  publicKeyValue: unknown,
  salt: Uint8Array,
  info: Uint8Array
): Promise<CryptoKey> {
  assertP256PrivateKey(privateKey);
  const publicKey = await importP256PublicKey(publicKeyValue);
  const sharedBuffer = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: publicKey },
    privateKey,
    256
  );
  const shared = new Uint8Array(sharedBuffer);
  try {
    const hkdfKey = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
    return await crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt, info },
      hkdfKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  } finally {
    shared.fill(0);
  }
}

export function validateRecoverySealedShare(value: unknown): RecoverySealedShare {
  const envelope = asRecord(value, 'Sealed recovery share');
  assertExactKeys(
    envelope,
    [
      'sealVersion',
      'protocolVersion',
      'suite',
      'sealId',
      'recipientPublicKeyFingerprint',
      'ephemeralPublicKeyJwk',
      'hkdfSalt',
      'iv',
      'ciphertext',
      'context'
    ],
    'Sealed recovery share'
  );
  if (
    envelope.sealVersion !== RECOVERY_SEAL_VERSION ||
    envelope.protocolVersion !== RECOVERY_PROTOCOL_VERSION ||
    envelope.suite !== RECOVERY_SEAL_SUITE
  ) {
    invalid('Unsupported sealed recovery share');
  }
  const context = validateRecoverySealContext(envelope.context);
  const recipientPublicKeyFingerprint = sha256Fingerprint(
    envelope.recipientPublicKeyFingerprint,
    'Sealed recovery recipient fingerprint'
  );
  if (
    (context.purpose === 'initial-share-custody' &&
      context.custodyPublicKeyFingerprint !== recipientPublicKeyFingerprint) ||
    (context.purpose === 'recovery-request-share' &&
      context.targetPublicKeyFingerprint !== recipientPublicKeyFingerprint)
  ) {
    invalid('Sealed recovery recipient does not match its context');
  }
  const sealId = decodeBase64Url(envelope.sealId, 'Recovery sealId', SEAL_ID_BYTES);
  const salt = decodeBase64Url(envelope.hkdfSalt, 'Recovery HKDF salt', HKDF_SALT_BYTES);
  const iv = decodeBase64Url(envelope.iv, 'Sealed recovery IV', AES_GCM_IV_BYTES);
  const ciphertext = decodeBase64Url(
    envelope.ciphertext,
    'Sealed recovery ciphertext',
    undefined,
    MAX_SEALED_PLAINTEXT_BYTES + 16
  );
  if (ciphertext.length < 17) {
    sealId.fill(0);
    salt.fill(0);
    iv.fill(0);
    ciphertext.fill(0);
    invalid('Sealed recovery ciphertext is too short');
  }
  sealId.fill(0);
  salt.fill(0);
  iv.fill(0);
  ciphertext.fill(0);
  return {
    sealVersion: RECOVERY_SEAL_VERSION,
    protocolVersion: RECOVERY_PROTOCOL_VERSION,
    suite: RECOVERY_SEAL_SUITE,
    sealId: envelope.sealId as string,
    recipientPublicKeyFingerprint,
    ephemeralPublicKeyJwk: normalizePublicKey(envelope.ephemeralPublicKeyJwk),
    hkdfSalt: envelope.hkdfSalt as string,
    iv: envelope.iv as string,
    ciphertext: envelope.ciphertext as string,
    context
  };
}

async function sealRecoveryPayload(
  plaintext: Uint8Array,
  recipientPublicKeyValue: unknown,
  contextValue: unknown
): Promise<RecoverySealedShare> {
  if (
    !isUint8Array(plaintext) ||
    plaintext.length === 0 ||
    plaintext.length > MAX_SEALED_PLAINTEXT_BYTES
  ) {
    invalid('Recovery sealed payload has an invalid length');
  }
  const recipientPublicKey = normalizePublicKey(recipientPublicKeyValue);
  const recipientPublicKeyFingerprint = await fingerprintRecoveryPublicKey(recipientPublicKey);
  const context = validateRecoverySealContext(contextValue);
  if (
    (context.purpose === 'initial-share-custody' &&
      context.custodyPublicKeyFingerprint !== recipientPublicKeyFingerprint) ||
    (context.purpose === 'recovery-request-share' &&
      context.targetPublicKeyFingerprint !== recipientPublicKeyFingerprint)
  ) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Recovery seal recipient does not match the bound context'
    );
  }

  const ephemeral = (await crypto.subtle.generateKey(
    ECDH_P256,
    false,
    ['deriveBits']
  )) as CryptoKeyPair;
  const ephemeralPublicKeyJwk = normalizePublicKey(
    await crypto.subtle.exportKey('jwk', ephemeral.publicKey)
  );
  const salt = randomBytes(HKDF_SALT_BYTES);
  const iv = randomBytes(AES_GCM_IV_BYTES);
  const sealId = randomBytes(SEAL_ID_BYTES);
  const header: Omit<RecoverySealedShare, 'ciphertext'> = {
    sealVersion: RECOVERY_SEAL_VERSION,
    protocolVersion: RECOVERY_PROTOCOL_VERSION,
    suite: RECOVERY_SEAL_SUITE,
    sealId: bytesToBase64Url(sealId),
    recipientPublicKeyFingerprint,
    ephemeralPublicKeyJwk,
    hkdfSalt: bytesToBase64Url(salt),
    iv: bytesToBase64Url(iv),
    context
  };
  const info = await sealKdfInfo(recipientPublicKeyFingerprint, ephemeralPublicKeyJwk, context);
  const aad = sealedShareAad(header);
  try {
    const key = await deriveSealKey(ephemeral.privateKey, recipientPublicKey, salt, info);
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
      key,
      plaintext
    );
    return {
      ...header,
      ciphertext: bytesToBase64Url(new Uint8Array(ciphertext))
    };
  } finally {
    salt.fill(0);
    iv.fill(0);
    sealId.fill(0);
    info.fill(0);
    aad.fill(0);
  }
}

async function openRecoveryPayload(
  envelopeValue: unknown,
  recipientPrivateKey: CryptoKey,
  expectedContextValue: unknown
): Promise<Uint8Array> {
  const envelope = validateRecoverySealedShare(envelopeValue);
  const expectedContext = validateRecoverySealContext(expectedContextValue);
  if (canonicalSerializeContext(envelope.context) !== canonicalSerializeContext(expectedContext)) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Sealed recovery share context does not match the expected request'
    );
  }

  const salt = decodeBase64Url(envelope.hkdfSalt, 'Recovery HKDF salt', HKDF_SALT_BYTES);
  const iv = decodeBase64Url(envelope.iv, 'Sealed recovery IV', AES_GCM_IV_BYTES);
  const ciphertext = decodeBase64Url(
    envelope.ciphertext,
    'Sealed recovery ciphertext',
    undefined,
    MAX_SEALED_PLAINTEXT_BYTES + 16
  );
  const info = await sealKdfInfo(
    envelope.recipientPublicKeyFingerprint,
    envelope.ephemeralPublicKeyJwk,
    envelope.context
  );
  const aad = sealedShareAad({
    sealVersion: envelope.sealVersion,
    protocolVersion: envelope.protocolVersion,
    suite: envelope.suite,
    sealId: envelope.sealId,
    recipientPublicKeyFingerprint: envelope.recipientPublicKeyFingerprint,
    ephemeralPublicKeyJwk: envelope.ephemeralPublicKeyJwk,
    hkdfSalt: envelope.hkdfSalt,
    iv: envelope.iv,
    context: envelope.context
  });
  try {
    let key: CryptoKey;
    try {
      key = await deriveSealKey(
        recipientPrivateKey,
        envelope.ephemeralPublicKeyJwk,
        salt,
        info
      );
    } catch (error) {
      if (error instanceof RecoveryCryptoError) throw error;
      throw new RecoveryCryptoError(
        'AUTHENTICATION_FAILED',
        'Sealed recovery share authentication failed'
      );
    }
    try {
      const plaintext = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
        key,
        ciphertext
      );
      return new Uint8Array(plaintext);
    } catch {
      throw new RecoveryCryptoError(
        'AUTHENTICATION_FAILED',
        'Sealed recovery share authentication failed'
      );
    }
  } finally {
    salt.fill(0);
    iv.fill(0);
    ciphertext.fill(0);
    info.fill(0);
    aad.fill(0);
  }
}

function parseSharePayload(plaintext: Uint8Array): RecoverySharePackage {
  try {
    let parsed: unknown;
    try {
      const json = new TextDecoder('utf-8', { fatal: true }).decode(plaintext);
      parsed = JSON.parse(json);
    } catch {
      invalid('Sealed recovery payload is not a valid share package');
    }
    return validateRecoverySharePackage(parsed);
  } finally {
    plaintext.fill(0);
  }
}

function assertShareMatchesContext(
  share: RecoverySharePackage,
  context: RecoverySealContext
): void {
  if (
    !bindingsMatch(share, context) ||
    share.membershipId !== context.membershipId ||
    share.index !== context.index ||
    share.custodyPublicKeyFingerprint !== context.custodyPublicKeyFingerprint
  ) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Decrypted recovery share does not match its authenticated context'
    );
  }
}

export async function sealRecoveryShareForCustody(
  shareValue: unknown,
  manifestValue: unknown,
  custodyPublicKeyValue: unknown
): Promise<RecoverySealedShare> {
  const share = validateRecoverySharePackage(shareValue);
  await assertShareMatchesManifest(share, manifestValue);
  const custodyFingerprint = await fingerprintRecoveryPublicKey(custodyPublicKeyValue);
  if (custodyFingerprint !== share.custodyPublicKeyFingerprint) {
    throw new RecoveryCryptoError(
      'CONTEXT_MISMATCH',
      'Custody public key does not match the share membership'
    );
  }
  const plaintext = utf8ToBytes(canonicalSerializeShare(share));
  try {
    return await sealRecoveryPayload(plaintext, custodyPublicKeyValue, contextFromShare(share));
  } finally {
    plaintext.fill(0);
  }
}

export async function openRecoveryShareFromCustody(
  envelopeValue: unknown,
  custodyPrivateKey: CryptoKey,
  expectedContextValue: unknown
): Promise<RecoverySharePackage> {
  const expectedContext = validateInitialContext(expectedContextValue);
  const share = parseSharePayload(
    await openRecoveryPayload(envelopeValue, custodyPrivateKey, expectedContext)
  );
  assertShareMatchesContext(share, expectedContext);
  return share;
}

function assertRequestNotExpired(context: RecoveryRequestShareContext, now: number): void {
  if (!Number.isFinite(now) || Date.parse(context.expiresAt) <= now) {
    throw new RecoveryCryptoError('EXPIRED_REQUEST', 'Recovery request has expired');
  }
}

export async function resealRecoveryShareForRequest(
  shareValue: unknown,
  manifestValue: unknown,
  targetPublicKeyValue: unknown,
  requestValue: unknown
): Promise<RecoverySealedShare> {
  const share = validateRecoverySharePackage(shareValue);
  await assertShareMatchesManifest(share, manifestValue);
  const context = await createRecoveryRequestContext(
    manifestValue,
    share.membershipId,
    targetPublicKeyValue,
    requestValue
  );
  assertRequestNotExpired(context, Date.now());
  const plaintext = utf8ToBytes(canonicalSerializeShare(share));
  try {
    return await sealRecoveryPayload(plaintext, targetPublicKeyValue, context);
  } finally {
    plaintext.fill(0);
  }
}

export function recoveryRequestReplayKey(contextValue: unknown): string {
  const context = validateRequestContext(contextValue);
  return JSON.stringify([
    'farfar-recovery-response-v1',
    context.circleId,
    context.circleVersion,
    context.manifestHash,
    context.requestId,
    context.membershipId,
    context.index
  ]);
}

export function createInMemoryRecoveryReplayGuard(): RecoveryReplayGuard {
  const consumed = new Set<string>();
  return {
    consume(replayKey: string): boolean {
      if (consumed.has(replayKey)) return false;
      consumed.add(replayKey);
      return true;
    }
  };
}

export async function openRecoveryShareForRequest(
  envelopeValue: unknown,
  targetPrivateKey: CryptoKey,
  expectedContextValue: unknown,
  replayGuard: RecoveryReplayGuard
): Promise<RecoverySharePackage> {
  if (!replayGuard || typeof replayGuard.consume !== 'function') {
    invalid('A recovery replay guard is required');
  }
  const expectedContext = validateRequestContext(expectedContextValue);
  assertRequestNotExpired(expectedContext, Date.now());
  const share = parseSharePayload(
    await openRecoveryPayload(envelopeValue, targetPrivateKey, expectedContext)
  );
  assertShareMatchesContext(share, expectedContext);
  const consumed = await replayGuard.consume(recoveryRequestReplayKey(expectedContext));
  if (!consumed) {
    throw new RecoveryCryptoError(
      'REPLAYED_REQUEST',
      'Recovery share response was already consumed for this request'
    );
  }
  return share;
}

export async function combineRecoveryShares(
  shareValues: readonly unknown[],
  manifestValue: unknown,
  vaultEnvelopeValue: unknown
): Promise<Uint8Array> {
  const manifest = validateRecoveryManifest(manifestValue);
  if (!Array.isArray(shareValues) || shareValues.length < manifest.threshold) {
    throw new RecoveryCryptoError(
      'INSUFFICIENT_SHARES',
      `Need at least ${manifest.threshold} recovery shares`
    );
  }
  if (shareValues.length > manifest.total) {
    invalid('Recovery share count exceeds manifest total');
  }

  const shares = shareValues.map(validateRecoverySharePackage);
  const indexes = new Set<number>();
  const memberships = new Set<string>();
  for (const share of shares) {
    await assertShareMatchesManifest(share, manifest);
    if (indexes.has(share.index) || memberships.has(share.membershipId)) {
      throw new RecoveryCryptoError('DUPLICATE_SHARE', 'Duplicate recovery share');
    }
    indexes.add(share.index);
    memberships.add(share.membershipId);
  }

  const rawShares: ShamirByteShare[] = [];
  let recoveryKey: Uint8Array | undefined;
  try {
    for (const share of shares) {
      rawShares.push({
        index: share.index,
        value: decodeBase64Url(
          share.shareValue,
          'Recovery share value',
          RECOVERY_KEY_BYTES
        )
      });
    }
    recoveryKey = combineSecretBytes(rawShares, manifest.threshold);
    return await unwrapAdultVaultKeyWithBinding(
      vaultEnvelopeValue,
      recoveryKey,
      await createRecoveryBinding(manifest)
    );
  } finally {
    for (const share of rawShares) share.value.fill(0);
    recoveryKey?.fill(0);
  }
}

export async function recoverAdultVaultKey(
  shareValues: readonly unknown[],
  manifestValue: unknown,
  vaultEnvelopeValue: unknown
): Promise<Uint8Array> {
  return combineRecoveryShares(shareValues, manifestValue, vaultEnvelopeValue);
}
